import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Flame, Mail, Phone, RefreshCcw, ExternalLink, Copy, Check, StickyNote, Loader2 } from 'lucide-react';
import PageHeader from '../components/PageHeader.jsx';
import ErrorBanner from '../components/ErrorBanner.jsx';
import { campaignsAPI } from '../services/api.js';
import { formatPhone } from '../lib/format.js';
import { SALES_STAGE_OPTIONS, FOLLOW_UP_STAGES, STAGES_PENDING_IN_CORE, stageLabel } from '../lib/salesStages.js';

// LEADS — every business that leaned in, in one place.
//
// Brian, 2026-08-13: "There was a leads area within the dashboard but I do not see it.
// Also there is a row for emails to send but that area isn't clickable so there is no
// way to get to those interested businesses."
//
// The second half was exactly right and worse than it sounds. A campaign sorted by sales
// stage grows a header row — "Send email · 5" — that is a plain <tr> with no click
// target and no matching filter. So the dashboard could TELL you five businesses were
// waiting on an email and give you no way to act on it. The count was the whole feature.
//
// The first half: no Leads page has ever existed here. The sales-stage machinery did,
// scattered one campaign at a time. This is that data pulled into one tenant-wide list.
//
// ⚠️ Sending is still MANUAL and deliberately so — nothing here sends anything. This
// page's only job is to make the follow-ups findable and their contact details
// copyable, which is the step that was missing.

// Stages that mean somebody is waiting on us. Ordered by how hot they are — an
// interested decision-maker outranks a maybe. Keep in sync with SALES_STAGES in
// core/src/services/campaignService.js and the list in CampaignDetail.jsx.
// Built from the shared funnel, so a stage added there appears here automatically.
const ACTIONABLE = FOLLOW_UP_STAGES.map((s) => ({ ...s, label: stageLabel(s.key) }));
const ORDER = new Map(ACTIONABLE.map((s, i) => [s.key, i]));



// ⛔ CACHE THE SCAN. Brian: "there is no caching done on the leads page. If I return
// back to the leads page ... it does another site wide search."
//
// Finding 9 leads costs a walk over 8,262 contacts across every campaign. Paying that
// again every time you come back — which is the normal rhythm, since the whole point of
// the page is to click into a lead and return — is both slow and a pile of needless
// requests. Module scope, so it survives navigation within the session but never
// outlives a reload, and Refresh always forces a real rebuild.
//
// TTL is deliberately short: a sales stage changes by hand, and showing someone a lead
// they already dealt with is worse than a two-second wait.
const CACHE_TTL_MS = 5 * 60_000;
let CACHE = null; // { at, rows, scanned }

// WHEN DID THIS BECOME A LEAD. Brian: "we need to put dates on these leads as well.
// The date of interest."
//
// `updatedAt` is when the row last changed, which for these rows is when the sales
// stage was set by hand — i.e. the moment it became a lead. `lastCalledAt` is the
// other candidate and it is WORSE here: for three of the dental leads it still reads
// July while the call that produced the interest was 5 August, because the callback
// path never writes back to the contact row (open with Charlie). Preferring updatedAt
// means the date shown is the one that actually moved.
const interestDate = (c) => c.updatedAt || c.lastCalledAt || null;

/** "3 days ago" — the age is the actionable part; a six-week-old lead is a different
 *  job from a same-day one, and that only reads at a glance as elapsed time. */
function ageLabel(iso) {
  if (!iso) return null;
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (Number.isNaN(days)) return null;
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  const w = Math.floor(days / 7);
  return w < 9 ? `${w} weeks ago` : `${Math.floor(days / 30)} months ago`;
}
const dateLabel = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : null;

function CopyButton({ text, label }) {
  const [done, setDone] = useState(false);
  if (!text) return null;
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setDone(true);
          setTimeout(() => setDone(false), 1400);
        } catch { /* clipboard blocked — the text is on screen to select by hand */ }
      }}
      className="text-stone-600 hover:text-alfred-gold"
      title={`Copy ${label}`}
      aria-label={`Copy ${label}`}
    >
      {done ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
    </button>
  );
}

export default function Leads() {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [scanned, setScanned] = useState(0);
  const [cachedAt, setCachedAt] = useState(null);
  const [fromCache, setFromCache] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [moved, setMoved] = useState(null); // { name, label } — what just left the list
  const [openNote, setOpenNote] = useState(null);
  const [draft, setDraft] = useState({ notes: '', nextStep: '' });
  const [savingNote, setSavingNote] = useState(false);

  // ⛔ TWO FIELDS, AND THE DIFFERENCE IS THE WHOLE POINT.
  //
  // Brian: "we need a note section to add notes. These notes will tie into the business
  // and inform any future calls."
  //
  // `notes` is durable human context and core states plainly that it is NEVER injected
  // into a prompt. Notes alone would be a record that changes nothing about the next
  // call — the opposite of what was asked, and invisible until a call went out unchanged.
  // What DOES reach the AI is `metadata.nextStep`, read by the prompt builder as
  // briefing.nextStep. So both are offered, each labelled with what it actually does,
  // rather than one box that quietly picks the wrong one.
  const openNotesFor = (lead) => {
    setOpenNote(lead.id === openNote ? null : lead.id);
    setDraft({ notes: lead.notes || '', nextStep: lead.metadata?.nextStep || '' });
  };

  const saveNotes = async (lead) => {
    setSavingNote(true);
    setError('');
    try {
      await campaignsAPI.updateContact(lead.campaignId, lead.id, {
        notes: draft.notes.trim() || null,
        // The server object is .strict() — send only the key being edited so an
        // unrelated dossier field (owner, industry) is never blanked by omission.
        metadata: { nextStep: draft.nextStep.trim() || null }
      });
      const apply = (list) => list.map((r) => (r.id === lead.id
        ? { ...r, notes: draft.notes, metadata: { ...(r.metadata || {}), nextStep: draft.nextStep } }
        : r));
      setRows((cur) => apply(cur || []));
      if (CACHE) CACHE = { ...CACHE, rows: apply(CACHE.rows) };
      setOpenNote(null);
    } catch (err) {
      setError(err?.body?.message || err.message || 'Could not save the note.');
    } finally {
      setSavingNote(false);
    }
  };

  // Move a lead through the funnel from here, rather than opening its campaign to do
  // one dropdown. Brian: "I need to be able to select the sales funnel for the lead …
  // for some of them I've already sent emails with a demo number, others the marketing
  // PDF, the other needs a call back."
  //
  // Writes straight through to the contact (core validates against its own enum) and
  // updates the CACHE as well as the view — otherwise coming back to the page would
  // show the stage you just changed, unchanged.
  const setStage = async (lead, next) => {
    const prev = lead.salesStage;
    if (next === prev) return;
    setSavingId(lead.id);
    setMoved(null);
    try {
      await campaignsAPI.updateContact(lead.campaignId, lead.id, { salesStage: next || null });
      const apply = (list) => {
        const updated = list.map((r) => (r.id === lead.id ? { ...r, salesStage: next } : r));
        // A stage outside the follow-up set means it is no longer a lead — drop it,
        // and SAY so, because a row vanishing on you looks like a bug.
        return ORDER.has(next) ? updated : updated.filter((r) => r.id !== lead.id);
      };
      setRows((cur) => apply(cur || []));
      if (CACHE) CACHE = { ...CACHE, rows: apply(CACHE.rows) };
      // ⛔ SAY SO ON *EVERY* MOVE, not only when the row leaves the list.
      // Brian, 2026-08-20: "it's still not working when I try to select from the drop
      // down" — after a hard refresh and a cache clear. It WAS working: nine contacts
      // had saved, verified in the DB and in the API response. But this page groups by
      // stage, so a changed row silently teleports to a section further down, and the
      // group still on screen keeps showing "Send email" for the rows genuinely still
      // owed one. Saving with no acknowledgement, plus a row vanishing from where you
      // were looking, is indistinguishable from a dropdown that does nothing.
      // The old code understood this for rows leaving the list and said nothing for
      // rows moving WITHIN it — which is the commonest case by far.
      const label = SALES_STAGE_OPTIONS.find((x) => x.value === next)?.label || 'no stage';
      setMoved({
        name: lead.company || lead.name || formatPhone(lead.phoneE164),
        label,
        left: !ORDER.has(next)
      });
    } catch (err) {
      const notYet = err?.status === 400 && STAGES_PENDING_IN_CORE.has(next);
      setError(notYet
        ? `“${SALES_STAGE_OPTIONS.find((x) => x.value === next)?.label}” is not live yet — core still has to add it to its list of stages. Nothing was changed.`
        : (err?.body?.message || err.message || 'Could not change the stage.'));
    } finally {
      setSavingId(null);
    }
  };

  const load = async ({ force = false } = {}) => {
    if (!force && CACHE && Date.now() - CACHE.at < CACHE_TTL_MS) {
      setRows(CACHE.rows);
      setScanned(CACHE.scanned);
      setCachedAt(CACHE.at);
      setFromCache(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const r = await campaignsAPI.list({ includeArchived: false });
      const camps = (r?.data || []).filter((c) => !c.archived);
      const found = [];
      let seen = 0;
      const queue = [...camps];
      const worker = async () => {
        while (queue.length) {
          const c = queue.shift();
          try {
            const cr = await campaignsAPI.contacts(c.id, { limit: 5000 });
            const list = cr?.data || [];
            seen += list.length;
            for (const ct of list) {
              if (!ORDER.has(ct.salesStage)) continue;
              found.push({ ...ct, campaignId: c.id, campaignName: c.name, campaignStatus: c.status });
            }
          } catch { /* one campaign failing must not empty the whole list */ }
        }
      };
      await Promise.all([worker(), worker(), worker()]);
      found.sort((a, b) =>
        (ORDER.get(a.salesStage) - ORDER.get(b.salesStage))
        || (new Date(interestDate(b) || 0) - new Date(interestDate(a) || 0))
        || String(a.company || a.name || '').localeCompare(String(b.company || b.name || '')));
      CACHE = { at: Date.now(), rows: found, scanned: seen };
      setRows(found);
      setScanned(seen);
      setCachedAt(CACHE.at);
      setFromCache(false);
    } catch (err) {
      setError(err.message || 'Could not load leads.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const m = new Map();
    for (const r of rows || []) {
      if (!m.has(r.salesStage)) m.set(r.salesStage, []);
      m.get(r.salesStage).push(r);
    }
    return m;
  }, [rows]);

  const total = rows?.length || 0;

  return (
    <div className="px-6 md:px-10 py-8 max-w-6xl">
      <PageHeader
        title="Leads"
        subtitle="Every business that leaned in, across all campaigns"
        actions={
          <button type="button" onClick={() => load({ force: true })} disabled={loading} className="btn-ghost">
            <RefreshCcw size={14} />
            Refresh
          </button>
        }
      />

      {error && <div className="mb-6"><ErrorBanner message={error} /></div>}
      {moved && (
        <p className="mb-4 rounded border border-stone-700 bg-stone-900/60 px-3 py-2 text-xs text-stone-300">
          {moved.name} moved to “{moved.label}”
          {moved.left
            ? ' — it is no longer a follow-up, so it has left this list.'
            : ' — saved. It is now under that heading further down this page.'}
        </p>
      )}

      {loading && (
        <p className="text-sm text-stone-500">Looking through every campaign…</p>
      )}

      {!loading && total === 0 && !error && (
        <div className="card">
          <p className="text-sm text-stone-400">
            No contact is sitting in a follow-up stage right now
            {scanned > 0 && <> — {scanned.toLocaleString()} contacts checked</>}.
          </p>
          <p className="mt-2 text-xs text-stone-500">
            A lead appears here once its sales stage is set on the campaign page — interested,
            send email, schedule a demo or a trial.
          </p>
        </div>
      )}

      {!loading && total > 0 && (
        <>
          <p className="mb-5 text-sm text-stone-400">
            <span className="text-stone-100">{total}</span> waiting on a follow-up
            {scanned > 0 && <span className="text-stone-500"> · {scanned.toLocaleString()} contacts checked</span>}
            {cachedAt && (
              <span className="text-stone-500">
                {' · '}
                {fromCache
                  ? `cached — scanned ${new Date(cachedAt).toLocaleTimeString()}, Refresh to rescan`
                  : `scanned just now ${new Date(cachedAt).toLocaleTimeString()}`}
              </span>
            )}
            <span className="text-stone-500"> · nothing here sends anything — follow-ups are still sent by hand</span>
          </p>

          {ACTIONABLE.filter((s) => grouped.has(s.key)).map((stage) => {
            const list = grouped.get(stage.key);
            return (
              <section key={stage.key} className="card mb-5">
                <header className="mb-3 flex flex-wrap items-center gap-2">
                  {stage.hot
                    ? <Flame size={16} className="text-amber-400" />
                    : stage.key === 'send_email'
                      ? <Mail size={16} className="text-stone-400" />
                      : <Phone size={16} className="text-stone-400" />}
                  <h2 className={`text-lg ${stage.hot ? 'text-amber-300' : 'text-stone-100'}`}>{stage.label}</h2>
                  <span className={`badge text-[10px] ${stage.hot
                    ? 'border border-amber-500/30 bg-amber-500/15 text-amber-300'
                    : 'border border-stone-700 bg-stone-800 text-stone-400'}`}>{list.length}</span>
                  <span className="text-xs text-stone-500">{stage.note}</span>
                </header>

                <ul className="divide-y divide-stone-800">
                  {list.map((c) => (
                    <li key={c.id} className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 py-2 text-sm">
                      <div className="min-w-0">
                        {/* Straight to the contact, with its transcripts, on the campaign page. */}
                        <Link
                          to={`/campaigns/${c.campaignId}?contact=${encodeURIComponent(c.id)}&q=${encodeURIComponent(c.phoneE164 || c.company || c.name || '')}`}
                          className="text-stone-200 underline-offset-2 hover:text-alfred-gold hover:underline"
                        >
                          {c.company || c.name || formatPhone(c.phoneE164)}
                        </Link>
                        <span className="ml-2 text-xs text-stone-500">{c.campaignName}</span>
                        {interestDate(c) && (
                          <span
                            className="ml-2 text-xs text-stone-500"
                            title={`Interested as of ${new Date(interestDate(c)).toLocaleString()}`}
                          >
                            · {dateLabel(interestDate(c))}
                            <span className={
                              (Date.now() - new Date(interestDate(c)).getTime()) > 14 * 86400000
                                ? ' text-amber-400/80' : ' text-stone-600'
                            }> ({ageLabel(interestDate(c))})</span>
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-stone-400">
                        {c.phoneE164 && (
                          <span className="inline-flex items-center gap-1.5">
                            {formatPhone(c.phoneE164)}
                            <CopyButton text={c.phoneE164} label="phone number" />
                          </span>
                        )}
                        {c.email ? (
                          <span className="inline-flex items-center gap-1.5">
                            <a href={`mailto:${c.email}`} className="underline-offset-2 hover:text-alfred-gold hover:underline">
                              {c.email}
                            </a>
                            <CopyButton text={c.email} label="email address" />
                          </span>
                        ) : (
                          // Called out, because "send email" with no address on file is a
                          // task nobody can do — and it looks identical to one you can.
                          stage.key === 'send_email' && (
                            <span className="text-amber-300">no email on file</span>
                          )
                        )}
                        <button
                          type="button"
                          onClick={() => openNotesFor(c)}
                          className={`inline-flex items-center gap-1 ${c.notes || c.metadata?.nextStep ? 'text-alfred-gold' : 'text-stone-600 hover:text-alfred-gold'}`}
                          title={c.notes || c.metadata?.nextStep ? 'Notes on file' : 'Add a note'}
                        >
                          <StickyNote size={12} />
                        </button>
                        <select
                          value={c.salesStage || ''}
                          disabled={savingId === c.id}
                          onChange={(e) => setStage(c, e.target.value)}
                          className="field-input py-0.5 px-1.5 text-xs"
                          aria-label={`Sales stage for ${c.company || c.name || 'this lead'}`}
                          title="Move this lead through the funnel"
                        >
                          {SALES_STAGE_OPTIONS.map((st) => (
                            <option key={st.value || 'none'} value={st.value}>{st.label}</option>
                          ))}
                        </select>
                        <Link
                          to={`/campaigns/${c.campaignId}?contact=${encodeURIComponent(c.id)}&q=${encodeURIComponent(c.phoneE164 || c.company || c.name || '')}`}
                          className="text-stone-600 hover:text-alfred-gold"
                          title="Open in the campaign"
                        >
                          <ExternalLink size={12} />
                        </Link>
                      </div>
                      {openNote === c.id && (
                        <div className="mt-2 w-full space-y-3 rounded-lg border border-stone-700 bg-stone-900/60 p-3">
                          <div>
                            <label className="field-label text-xs" htmlFor={`n-${c.id}`}>Notes — for you</label>
                            <p className="mb-1 text-[11px] text-stone-500">
                              Standing context about this business. Kept forever, never overwritten by the
                              AI’s own call summary — and never read to anyone on a call.
                            </p>
                            <textarea
                              id={`n-${c.id}`} rows={3} className="field-input w-full text-sm"
                              value={draft.notes}
                              onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
                              placeholder="e.g. They already run their own AI receptionist and it handled the call well."
                            />
                          </div>
                          <div>
                            <label className="field-label text-xs" htmlFor={`s-${c.id}`}>What the AI is told next time</label>
                            <p className="mb-1 text-[11px] text-amber-300/80">
                              This one DOES go into the next call, as its objective. Leave it empty and the
                              campaign’s own pitch is used.
                            </p>
                            <textarea
                              id={`s-${c.id}`} rows={2} className="field-input w-full text-sm"
                              value={draft.nextStep}
                              onChange={(e) => setDraft((d) => ({ ...d, nextStep: e.target.value }))}
                              placeholder="e.g. Don’t pitch — ask who supplied their current AI and what it can’t do."
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <button type="button" className="btn-primary" onClick={() => saveNotes(c)} disabled={savingNote}>
                              {savingNote ? <Loader2 size={13} className="animate-spin" /> : null} Save
                            </button>
                            <button type="button" className="btn-ghost" onClick={() => setOpenNote(null)}>Cancel</button>
                          </div>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </>
      )}
    </div>
  );
}
